import { inject, NgModule } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { ServerError } from '@apollo/client/errors';
import { Router } from '@angular/router';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';

import { environment } from '@environments/environment';
import { SessionService } from '@app/core/services/session/session.service';
import {
  projectPendingChanges,
  projectPendingChangesKeys,
  surveyQuestionsPendingChanges
} from '@app/core/services/project-changes/project-changes.service';
import { UtilityService } from '@app/shared/services/utility/utility.service';
import { LocalState } from '@apollo/client/local-state';

const uri = environment.graphqlUri;
export function createApollo(): ApolloClient.Options {
  const sessionService = inject(SessionService);
  const router = inject(Router);
  const util = inject(UtilityService);
  const authInterceptor = new SetContextLink((_prevContext, _operation) => {
    const session = sessionService.getSession();
    // if a session exists, set the session
    // token in the authorization header
    if (!session) {
      sessionService.destroySession();
      return {};
    }
    else {
      return {
        headers: {
          Authorization: session.token
        }
      };
    }
  });

  const errorHandler = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path, extensions }) => {
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
        // if the error is related to authentication, navigate to the signin page
        if (extensions?.code === 'UNAUTHENTICATED_ERROR') {
          sessionService.destroySession();
          router.navigateByUrl('/signin');
        }
        else {
          // otherwise navigate to the error page
          router.navigate(['/error'], { queryParams: { path: encodeURIComponent(window.location.pathname) }});
        }
      });
    }
    else if (ServerError.is(error)) {
      console.log(`[Network error]: ${JSON.stringify(error)}`);
      if (error.statusCode === 401) {
        sessionService.destroySession();
        router.navigateByUrl('/signin');
      }
      // else navigate to the error page
      else {
        router.navigate(['/error'], { queryParams: { path: encodeURIComponent(window.location.pathname) }});
      }
    }
    else if (error) {
      console.log(`[Network error]: ${JSON.stringify(error)}`);
      router.navigate(['/error'], { queryParams: { path: encodeURIComponent(window.location.pathname) }});
    }
  });

  return {
    link: ApolloLink.from([
      authInterceptor,
      errorHandler,
      new UploadHttpLink({ uri })
    ]),
    cache: createCache(util),
    localState: new LocalState()
  };
}

@NgModule({
  providers: [
    provideApollo(createApollo),
  ],
})
export class GraphQLModule {}

// create and return a new InMemoryCache
export function createCache(util: UtilityService): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // query type
      Query: {
        fields: {
          // define the policy for merging userInsightProjects
          userInsightProjects: {
            merge(existing, incoming) {
              // overwrite any existing projects with the
              // incoming new projects
              return incoming;
            }
          },
          // define the policy for merging userProjects
          listUserProjects: {
            merge(existing, incoming) {
              // overwrite any existing projects with the
              // incoming new projects
              return incoming;
            }
          },
          // define the policy for merging projectInvestmentOpportunityResults
          projectInvestmentOpportunityResults: {
            merge(existing, incoming) {
              // overwrite any existing results with the
              // incoming new results
              return incoming;
            }
          },
          // define the policy for merging resultsTable
          resultsTable: {
            merge(existing, incoming) {
              // overwrite any existing results with the
              // incoming new results
              return incoming;
            }
          }
        }
      },
      // project type
      Project: {
        fields: {
          // define the policy for reading the project pending
          // changes reactive variable
          pendingChanges: {
            read() {
              return projectPendingChanges();
            }
          },
          // define the policy for reading the project pending
          // changes keys reactive variable
          pendingChangesKeys: {
            read() {
              return projectPendingChangesKeys();
            }
          },
          // define the policy for reading the survey question
          // pending changes reactive variable
          surveyQuestionsPendingChanges: {
            read() {
              return surveyQuestionsPendingChanges();
            }
          },
          // define policies for the landArea field
          landArea: {
            // when reading the land area value, convert it from m2 to ft2
            read(value) {
              return util.m2ToFt2(value);
            }
          },
          // define policies for the roofArea field
          roofArea: {
            // when reading the roof area value, convert it from m2 to ft2
            read(value) {
              return util.m2ToFt2(value);
            }
          }
        }
      },
      // SurveyQuestion type
      SurveyQuestion: {
        fields: {
          // define the policy for merging surveyAnswers
          surveyAnswers: {
            merge(existing, incoming) {
              // overwrite any existing survey answers with the
              // incoming new survey answers
              return incoming;
            }
          }
        }
      }
    }
  });
}
